<template>
	<div tabindex="0" class="hide-outline">
		<main class="main-view no-top-panel">
			<div>
				<div class="title">
					{{ roundPlanningId != 0 ? $t('roundplanningdetail.edit') : $t('roundplanningdetail.new') }} {{ $t('roundplanningdetail.title_suffix') }}
					{{
						" - " + (roundPlanning.customerName || getActiveCustomer.name || "")
					}}
				</div>
				<div class="top-box">
					<div class="row" :style="mobile">
						<div class="field">
							<label class="label">{{ $t('roundplanningdetail.name_label') }}</label>
							<div class="control">
								<textarea
									v-model="localRoundPlanning.name"
									class="textarea no-resize"
									rows="2"
									:placeholder="$t('roundplanningdetail.name_placeholder')"
								/>
							</div>
						</div>
						<div class="field description">
							<label class="label">{{ $t('roundplanningdetail.description_label') }}</label>
							<div class="control">
								<textarea
									v-model="localRoundPlanning.description"
									class="textarea no-resize"
									rows="2"
									:placeholder="$t('roundplanningdetail.description_placeholder')"
								/>
							</div>
						</div>
						<div class="buttons" v-if="!isMobile">
							<button
								class="button is-info"
								:class="loading ? 'is-loading' : ''"
								@click="save"
								:disabled="disableSave"
							>
								<span class="icon is-white"><i class="fas fa-save"/></span
								><span>{{ $t('roundplanningdetail.save_button') }}</span></button
							><button class="button is-dark" @click="cancel">
								<span class="icon"><i class="fas fa-backward"/></span>
								<span>{{ $t('roundplanningdetail.back_button') }}</span>
							</button>
							<button
								v-if="$isAllowedTo('global_set_object_as_global')"
								class="button is-warning"
								:class="forceActiveLoading ? 'is-loading' : ''"
								@click="forceActive"
								>
								<span class="icon"><i class="fas fa-bolt"/></span>
								<span>{{ $t('roundplanningdetail.force_active') }}</span>
							</button>

						</div>
					</div>
					<div class="row" :style="mobile">
						<div class="field date" :class="{ 'is-mobile': isMobile }">
							<label class="label">{{ $t('roundplanningdetail.start_date') }}</label>
							<div class="control">
								<v-date-picker
									v-model="localRoundPlanning.start"
									:input-props="{
										class: 'input date-input'
									}"
									:popover="{ visibility: 'click' }"
									color="orange"
								/>
							</div>
						</div>
						<div class="field responsible">
							<label class="label">{{ $t('roundplanningdetail.responsible') }}</label>
							<div class="control">
								<v-select
									class="panel-select role"
									v-model="selectedRole"
									:options="dropdownRoles"
									:placeholder="$t('roundplanningdetail.select_role')"
									:allowEmpty="false"
									:internalSearch="true"
									:showLabels="false"
									track-by="id"
									label="name"
								>
									<span slot="noResult">{{ $t('roundplanningdetail.no_results') }}</span>
									<span
										slot="caret"
										slot-scope="{ toggle }"
										class="caret"
										@mousedown.prevent.stop="toggle()"
									><i class="fas fa-caret-down caret-inner"></i
									></span>
								</v-select>
							</div>
						</div>
						<div class="switches" :class="{ 'is-mobile': isMobile }">
							<div class="field status">
								<label class="label">
									{{ localRoundPlanning.active ? $t('roundplanningdetail.active') : $t('roundplanningdetail.inactive') }}</label
								>
								<div class="control">
									<switchButton v-model="localRoundPlanning.active" />
								</div>
							</div>
							<div class="field email">
								<label class="label">{{ $t('roundplanningdetail.reminders') }}</label>
								<div class="control">
									<switchButton v-model="localRoundPlanning.sendMessage" />
								</div>
							</div>
							<div class="field email">
								<label class="label">{{ $t('roundplanningdetail.fire_protection') }}</label>
								<div class="control">
									<switchButton v-model="withFireProtections" />
								</div>
							</div>
						</div>
						<div
							v-if="localRoundPlanning.sendMessage"
							class="field reminder-role"
							:class="{ 'is-mobile': isMobile }"
						>
							<label class="label label-with-tooltip">
								<span>{{ $t('roundplanningdetail.second_reminder_role') }}</span>
								<span
									class="tooltip reminder-role-tooltip"
									tabindex="0"
									:aria-label="$t('roundplanningdetail.second_reminder_role_tooltip')"
								>
									<i class="far fa-question-circle" />
									<span class="tooltip-text">
										{{ $t('roundplanningdetail.second_reminder_role_tooltip') }}
									</span>
								</span>
							</label>
							<div class="control">
								<v-select
									class="panel-select role"
									v-model="selectedEscalationRole"
									:options="dropdownRoles"
									:placeholder="$t('roundplanningdetail.select_second_reminder_role')"
									:allowEmpty="true"
									:internalSearch="true"
									:showLabels="false"
									track-by="id"
									label="name"
								>
									<span slot="noResult">{{ $t('roundplanningdetail.no_results') }}</span>
									<span
										slot="caret"
										slot-scope="{ toggle }"
										class="caret"
										@mousedown.prevent.stop="toggle()"
									><i class="fas fa-caret-down caret-inner"></i
									></span>
								</v-select>
							</div>
						</div>
						<div class="mini-block" :class="isMobile ? 'is-mobile' : ''">
							<div class="field interval">
								<label class="label">{{ $t('roundplanningdetail.interval') }}</label>
								<div class="control">
									<input
										type="number"
										min="1"
										class="input"
										v-model.number="localRoundPlanning.intervalValue"
									/>
									<div class="select">
										<select v-model.number="localRoundPlanning.intervalType">
											<option value="3">{{ $t('roundplanningdetail.months') }}</option>
											<option value="4">{{ $t('roundplanningdetail.years') }}</option>
										</select>
									</div>
								</div>
							</div>
							<div
								class="buttons"
								:class="{ 'is-mobile': isMobile }"
								v-if="isMobile"
							>
								<button
									class="button is-info"
									:class="loading ? 'is-loading' : ''"
									:disabled="disableSave"
									@click="save"
								>
									<span class="icon is-white"><i class="fas fa-save"/></span
									><span>{{ $t('roundplanningdetail.save_button') }}</span>
								</button>
								<button
									v-if="$isAllowedTo('global_set_object_as_global')"
									class="button is-warning"
									:class="forceActiveLoading ? 'is-loading' : ''"
									:disabled="disableForceActive"
									@click="forceActive"
									>
									<span class="icon"><i class="fas fa-bolt"/></span>
									<span>{{ $t('roundplanningdetail.force_active') }}</span>
								</button>
							</div>
						</div>
					</div>
				</div>

				<ui-alert v-if="forceActiveError" error class="mb-3">{{ forceActiveError }}</ui-alert>
				<TabComponent :tabs="tabs" fullwidth v-model="selectedTab" />
			</div>
			<div class="lists columns" v-if="showLists">
				<SelectionList
					:list-items="comAddList"
					:title="comTitle"
					:tab="selectedTab"
					:mode="'add'"
					:useDropdownInsteadOfSearch="selectedTab == 0"
					:dropdownOptions="buildings"
					:dropdownPlaceholder="$t('roundplanningdetail.select_building_placeholder')"
					:dropdownTitle="$t('roundplanningdetail.building_label')"
					:displayName="selectedTab === 2 ? 'displayName' : 'name'"
					@selectItem="addItem"
					@search="searchAdd"
					@changeDropdownSelection="selectBuilding"
					@searchDropdown="getCustomerBuildings"
					/>

					<SelectionList
					:list-items="comAddedList"
					:title="comTitle"
					:tab="selectedTab"
					:blankSelection="blankSelectionAddedList"
					:mode="'remove'"
					:displayName="selectedTab === 2 ? 'displayName' : 'name'"
					@selectItem="removeItem"
					@search="searchAdded"
				/>
			</div>
		</main>
	</div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import SelectionList from '@/components/SelectionList/SelectionList';
import TabComponent from '@/components/TabComponent';
import switchButton from '@/components/switchButton';
import typeMixin from '@/mixins/iconMixin';
export default {
	mixins: [typeMixin], 
	components: {
		SelectionList,
		TabComponent,
		switchButton
	},
	data() {
		return {
			roundPlanningId: 0,
			forceActiveLoading: false,
			forceActiveError: '',
			selectedTab: 0,
			localRoundPlanning: {
				role: {},
				fireProtections: [],
				areas: [],
				floors: [],
				questionLists: [],
				roles: []
			},
			selectedRole: {},
			selectedEscalationRole: null,
			selectedBuilding: { floors: [] },
			loading: false,
			showLists: false,
			addedSearchValue: '',
			withFireProtections: null,
		};
	},

	computed: {
		...mapGetters('appStore', ['isMobile']),
		...mapGetters('customerStore', ['customer']),
		...mapGetters('roleStore', ['dropdownRoles', 'roles', 'roundRoles']),
		...mapGetters('roundPlanningStore', ['roundPlanning']),
		...mapGetters('fireProtectionStore', [
			'listFireProtections',
			'roundFireProtections'
		]),
		...mapGetters('questionListStore', ['questionLists', 'roundQuestionLists']),
		...mapGetters('buildingStore', ['buildings']),
		...mapGetters('areaStore', ['areas', 'roundAreas']),
		...mapGetters('user', ['getActiveCustomer', 'getActiveCompany']),
		...mapGetters('floorStore', ['roundFloors']),

		disableForceActive() {
			return this.loading || this.forceActiveLoading || !this.localRoundPlanning?.id;
		},

		blankSelectionAddedList() {
			return this.tab == 0 || this.tab == 1 || this.tab == 2 || this.tab == 3 || this.tab == 4
				? this.$t('roundplanningdetail.none_selected', { item: this.comTitle })
				: this.$t('roundplanningdetail.all_selected', { item: this.comTitle });
		},
		mobile() {
			return this.isMobile ? { display: 'block' } : '';
		},
		disableSave() {
			return this.loading || !this.localRoundPlanning.name;
		},
		comTitle() {
			switch (this.selectedTab) {
				case 0: return this.$t('roundplanningdetail.floors');
				case 1: return this.$t('roundplanningdetail.areas');
				case 2: return this.$t('roundplanningdetail.fire_protections');
				case 3: return this.$t('roundplanningdetail.checklists');
				case 4: return this.$t('roundplanningdetail.executors');
			}
			return '';
		},
		comAddList() {
			switch (this.selectedTab) {
			case 0:
				return this.comFloors || [];
			case 1:
				return this.comAreas || [];
			case 2:
				return this.comFireProtections || [];
			case 3:
				return this.comQuestionLists || [];
			case 4:
				return this.comRoles || [];
			}

			return [];
		},
	 comAddedList() {
			let list = [];
			switch (this.selectedTab) {
			case 0: list = this.localRoundPlanning.floors || []; break;
			case 1: list = this.localRoundPlanning.areas || []; break;
			case 2:
				list = (this.localRoundPlanning.fireProtections || []).map(fp => ({
				...fp,
				displayName: this._safeFPDisplay(fp),
				}));
				break;
			case 3: list = this.localRoundPlanning.questionLists || []; break;
			case 4: list = this.localRoundPlanning.roles || []; break;
			}

			if (this.addedSearchValue) {
			list = list.filter(e =>
				(e.displayName || e.name || '').toLowerCase().includes(this.addedSearchValue)
			);
			}

			list.sort((a, b) =>
			(a.displayName || a.name || '').toLowerCase()
				.localeCompare((b.displayName || b.name || '').toLowerCase())
			);

			return list;
		},
		comQuestionLists() { 
			let selectedQuestionLists = this.roundQuestionLists;

			if (selectedQuestionLists) {
				let newQuestionLists = this.questionLists.filter(
					this.comparer(selectedQuestionLists)
				);

				return newQuestionLists;
			}

			return this.questionLists;
		},
		comFireProtections() {
			let fps = this.listFireProtections;
			const selected = this.roundFireProtections;
			if (selected) fps = fps.filter(this.comparer(selected));

			return fps.map(fp => {
			const label = this._safeFPDisplay(fp);
			return {
				...fp,
				displayName: label,
				name: label,               
			};
			});
		},
		comRoles() {
			let roles = this.roles;
			let selectedRoles = this.localRoundPlanning.roles;

			if (selectedRoles) {
				let newRoles = roles.filter(this.comparer(selectedRoles));

				return newRoles;
			}

			return roles;
		},
		comFloors() {
			if (!this.selectedBuilding) return [];

			let floors = this.selectedBuilding.floors;

			let selectedFloors = this.roundFloors;

			if (selectedFloors) {
				let newFloors = floors.filter(this.comparer(selectedFloors));

				return newFloors;
			}

			return floors;
		},
		comAreas() {
			let areas = this.areas;
			let selectedAreas = this.roundAreas;

			if (selectedAreas) {
				let newAreas = areas.filter(this.comparer(selectedAreas));

				return newAreas;
			}

			return areas;
		},

		tabs() {
			return [
			{ name: this.$t('roundplanningdetail.tab_floors'), icon: 'fa-building', tag: '' },
			{ name: this.$t('roundplanningdetail.tab_areas'), icon: 'fa-map', tag: '' },
			{ name: this.$t('roundplanningdetail.tab_fire_protection'), icon: 'fa-fire-extinguisher', tag: '' },
			{ name: this.$t('roundplanningdetail.tab_checklists'), icon: 'fa-tasks', tag: '' },
			{ name: this.$t('roundplanningdetail.tab_executors'), icon: 'fa-user-tag', tag: '' }
			];
		}
	},
	watch: {
		roundFloors: {
			handler(to) {
				this.tabs[0].tag = to.length;
			}
		},
		roundAreas: {
			handler(to) {
				this.tabs[1].tag = to.length;
			}
		},
		roundFireProtections: {
			handler(to) {
				this.tabs[2].tag = to.length;
			}
		},
		roundQuestionLists: {
			handler(to) {
				this.tabs[3].tag = to.length;
			}
		},
		roundRoles: {
			handler(to) {
				this.tabs[4].tag = to.length;
			}
		},
		'getActiveCustomer.id'() {
			this.$router.push({ name: 'RoundPlanningView' });
		}
	},
	methods: {

		...mapActions('roleStore', [
			'getDropdownRoles',
			'getAllRoles',
			'getRoundRoles'
		]),
		...mapActions('roundPlanningStore', [
			'getRoundPlanning',
			'updateRoundPlanning',
			'createRoundPlanning',
			'forceUpdate'
			]),
		...mapActions('floorStore', ['getRoundFloors']),
		...mapActions('customerStore', ['getCustomer']),
		...mapActions('fireProtectionStore', [
			'getListFireProtections',
			'getRoundFireProtections'
		]),
		...mapActions('questionListStore', [
			'getAllQuestionLists',
			'getRoundQuestionLists'
		]),
		...mapActions('buildingStore', ['getCustomerBuildings']),
		...mapActions('areaStore', ['getAllAreas', 'getRoundAreas']),
		...mapMutations('roleStore', ['SET_ROUND_ROLES']),
		...mapMutations('floorStore', ['SET_ROUND_FLOORS']),
		...mapMutations('fireProtectionStore', ['SET_ROUND_FIREPROTECTIONS']),
		...mapMutations('questionListStore', ['SET_ROUND_QUESTIONLISTS']),
		...mapMutations('areaStore', ['SET_ROUND_AREAS']),
		prepareForceActive(plan) {
			plan.active = true;
			return plan;
		},
			
		comparer(otherArray) {
			return function(current) {
				return (
					otherArray.filter(function(other) {
						return other.id === current.id;
					}).length === 0
				);
			};
		},
		cancel() {
			this.$router.push(`/${this.$i18n.locale}/roundplanning`);
		},
		save() {
			if (this.loading || !this.localRoundPlanning.name) return;

			const capability = this.localRoundPlanning.id ? 'roundplannings_edit' : 'roundplannings_create';
			if (!this.$isAllowedTo(capability)) return;

			this.loading = true;
			let plan = this.localRoundPlanning;
			plan.start = this.convertDate(plan.start);
			plan.ignoreItems = !this.withFireProtections;

			function getId(element) {
				return element.id;
			}

			if (plan.intervalValue <= 0) {
				plan.intervalValue = 1;
			}

			plan.fireProtectionIds = plan.fireProtections.map(getId);
			plan.areaIds = plan.areas.map(getId);
			plan.floorIds = plan.floors.map(getId);
			plan.questionListIds = plan.questionLists.map(getId);
			plan.roleIds = plan.roles.map(getId);
			plan.escalationRoleId = this.selectedEscalationRole?.id || null;

			if (!plan.customerId && this.getActiveCustomer?.id) {
				plan.customerId = this.getActiveCustomer?.id;
			}

			plan.roleId = this.selectedRole.id;

			if (plan.id) {
				this.updateRoundPlanning(plan)
					.then(() => {
						this.loading = false;
					})
					.catch(err => {
						console.log(err);
						this.loading = false;
					});
			} else {
				this.createRoundPlanning(plan)
					.then(result => {
						if (!result?.id) throw new Error('Round planning was not created.');

						this.$set(this.localRoundPlanning, 'id', result.id);
						this.roundPlanningId = result.id;
						this.localRoundPlanning.start = this.convertDate(new Date(result.start));
						this.selectedRole = result.responsible || this.selectedRole;
						this.selectedEscalationRole = result.escalationRole || null;
						this.loading = false;
						this.$router.replace({
							name: 'RoundPlanningDetailView',
							params: { roundPlanningId: this.roundPlanningId }
						});
					})
					.catch(err => {
						console.log(err);
						this.loading = false;
					});
			}
		},
		async forceActive() {
			this.forceActiveLoading = true;
			this.forceActiveError = '';

			try {
				let plan = { ...this.localRoundPlanning };

				plan.ignoreItems = !this.withFireProtections;

				const getId = e => e.id;

				plan.fireProtectionIds = (plan.fireProtections || []).map(getId);
				plan.areaIds = (plan.areas || []).map(getId);
				plan.floorIds = (plan.floors || []).map(getId);
				plan.questionListIds = (plan.questionLists || []).map(getId);
				plan.roleIds = (plan.roles || []).map(getId);
				plan.escalationRoleId = this.selectedEscalationRole?.id || null;

				if (!plan.customerId && this.getActiveCustomer?.id) {
					plan.customerId = this.getActiveCustomer.id;
				}

				plan.roleId = this.selectedRole?.id;

				plan = this.prepareForceActive(plan);

				await this.forceUpdate(plan);

				await this.getRoundPlanning(this.roundPlanningId);
				this.localRoundPlanning = { ...this.roundPlanning };
			} catch (err) {
				const errorKey = err?.message;
				this.forceActiveError = errorKey && this.$te(errorKey)
					? this.$t(errorKey)
					: (errorKey || this.$t('axios.genericError'));
			} finally {
				this.forceActiveLoading = false;
			}
		},

		convertDate(date) {
			const d = date instanceof Date ? date : new Date(date);

			if (isNaN(d.getTime())) {
				const now = new Date();
				return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
			}

			return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
		},

		addItem(item) {
			switch (this.selectedTab) {
			case 0:
				this.localRoundPlanning.floors.push(item);
				break;
			case 1:
				this.localRoundPlanning.areas.push(item);
				break;
			case 2:
				this.localRoundPlanning.fireProtections.push(item);
				break;
			case 3:
				this.localRoundPlanning.questionLists.push(item);
				break;
			case 4:
				this.localRoundPlanning.roles.push(item);
				break;
			}
		},
		removeItem(item, index) {
			switch (this.selectedTab) {
			case 0:
				this.localRoundPlanning.floors.splice(index, 1);
				break;
			case 1:
				this.localRoundPlanning.areas.splice(index, 1);
				break;
			case 2:
				this.localRoundPlanning.fireProtections.splice(index, 1);
				break;
			case 3:
				this.localRoundPlanning.questionLists.splice(index, 1);
				break;
			case 4:
				this.localRoundPlanning.roles.splice(index, 1);
				break;
			}
		},
		searchAdd(searchValue) {
			switch (this.selectedTab) {
			case 0:
				break;
			case 1:
				this.getAllAreas({
					customerId: this.getActiveCustomer?.id,
					search: searchValue
				});
				break;
			case 2:
				this.getListFireProtections(searchValue);
				break;
			case 3:
				this.getAllQuestionLists({
					description: searchValue,
					customerId: this.getActiveCustomer?.id
				});
				break;
			case 4:
				this.getAllRoles({
					customerId: this.getActiveCustomer?.id,
					search: searchValue
				});
				break;
			}
		},
		selectBuilding(building) {
			this.selectedBuilding = building;
		},
		searchAdded(value) {
			this.addedSearchValue = value.toLowerCase();
		},
		_safeFPDisplay(fp) {
			const obj = fp?.fireProtection || fp || {};

			const preferred = this.displayType(obj);
			if (preferred) return preferred;

			const byType = this.tTypeKey(obj.category ?? obj.key ?? obj.fireProtectionType ?? obj.inventoryType);
			return obj.name || obj.title || byType || this.$t('common.unnamed');
		}
	},
	created() {

		this.roundPlanningId = this.$route.params.roundPlanningId;

		if (!this.getActiveCustomer?.id && this.roundPlanningId == 0) {
			this.$router.push(`/${this.$i18n.locale}/roundplanning`);
		}

		this.getListFireProtections('');

		if (this.$isAllowedTo('checklists_access_view')) {
			this.getAllQuestionLists({
				description: '',
				customerId: this.getActiveCustomer?.id
			});
		} else {
			this.tabs[3].disabled = true;
		}

		if (this.roundPlanningId != 0) {
			this.getRoundPlanning(this.roundPlanningId).then(() => {
				this.getCustomer(this.roundPlanning.customerId).then(() => {
					this.getDropdownRoles(this.getActiveCustomer?.id);
					this.getAllRoles({
						customerId: this.getActiveCustomer?.id,
						search: ''
					});
					this.getAllAreas({
						customerId: this.getActiveCustomer?.id,
						search: ''
					});
					this.getCustomerBuildings().then(() => {
						this.selectedBuilding = this.buildings[0];
						this.showLists = true;
					});
				});
				this.localRoundPlanning = Object.assign({}, this.roundPlanning);

				this.getRoundFloors(this.roundPlanningId).then(() => {
					this.localRoundPlanning.floors = this.roundFloors;
				});
				this.getRoundAreas(this.roundPlanningId).then(() => {
					this.localRoundPlanning.areas = this.roundAreas;
				});
				this.getRoundFireProtections(this.roundPlanningId).then(() => {
					this.localRoundPlanning.fireProtections = this.roundFireProtections;
				});
				this.getRoundQuestionLists(this.roundPlanningId).then(() => {
					this.localRoundPlanning.questionLists = this.roundQuestionLists;
				});
				this.getRoundRoles(this.roundPlanningId).then(() => {
					this.localRoundPlanning.roles = this.roundRoles;
				});

				this.selectedRole = this.localRoundPlanning.responsible;
				this.selectedEscalationRole = this.localRoundPlanning.escalationRole || null;
				this.localRoundPlanning.start = this.convertDate(
					new Date(this.localRoundPlanning.start)
				);
				this.withFireProtections = !this.localRoundPlanning.ignoreItems;
			});
		} else {
			this.SET_ROUND_ROLES([]);
			this.SET_ROUND_FLOORS([]);
			this.SET_ROUND_FIREPROTECTIONS([]);
			this.SET_ROUND_QUESTIONLISTS([]);
			this.SET_ROUND_AREAS([]);
			this.localRoundPlanning.start = new Date();
			this.localRoundPlanning.intervalValue = 0;
			this.localRoundPlanning.intervalType = 3;
			this.localRoundPlanning.active = true;
			this.withFireProtections = true;
			this.selectedEscalationRole = null;
			this.getDropdownRoles(this.getActiveCustomer?.id).then(() => {
				this.selectedRole = this.dropdownRoles[0];
			});
			this.getCustomerBuildings().then(() => {
				this.selectedBuilding = this.buildings[0];
				this.showLists = true;
			});
		}
	}
};
</script>

<style lang="scss" scoped>
@import "@/assets/colors.scss";

.column .title {
  margin-bottom: 16px;
}
.top-box {
  background: $lightest-grey;
  padding: 10px 5px 10px 5px;
  display: block;
  border-radius: 2px;
  margin-bottom: 10px;
  .row {
    display: flex;
    position: relative;
  }
  .switches {
    display: flex;
    flex-wrap: none;
    max-width: 268px;
    &.is-mobile {
      margin-top: 10px;
    }
  }
  .reminder-role {
    min-width: 240px;
    margin: 0 10px;
    &.is-mobile {
      margin: 10px 5px 0 5px;
    }
  }
  .label-with-tooltip {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }
  .reminder-role-tooltip {
    display: inline-flex;
    align-items: center;
    color: $light-grey;
    .fas,
    .far {
      font-size: 0.9rem;
    }
    .tooltip-text {
      min-width: 260px;
      max-width: 320px;
      left: auto;
      right: -12px;
      top: 28px;
      text-align: left;
      white-space: normal;
      line-height: 1.35;
    }
    .tooltip-text::after {
      left: auto;
      right: 12px;
    }
    &:hover,
    &:focus-within {
      color: $info-active;
    }
  }
  .mini-block {
    display: flex;
    flex: 0.3;
    &.is-mobile {
      flex-wrap: wrap;
      .status,
      .interval {
        flex: 0;
        margin: 0 5px;
        .label {
          margin-left: 0;
        }
      }
      .status,
      .email {
        margin-top: -0.7rem;
      }
    }
    .email {
      .label {
        margin-left: -6px;
      }
    }
    .status {
      flex: 0.22;
      margin-left: 26px;
      .label {
        margin-left: 8px;
      }
    }

    .interval {
      flex: 0.5;
      .control {
        .input {
          background: white;
          width: 45px;
          border-radius: 2px;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          text-align: right;
        }
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .select select {
          background: white;
          border-radius: 2px;
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
          border-left: none;
          box-shadow: inset 0 0.0625em 0.125em rgba(10, 10, 10, 0.05);
        }
        display: flex;
      }
    }
    .buttons.is-mobile {
      margin-top: 1.4rem;
      .button {
        margin-bottom: 0;
      }
    }
  }
  .field {
    flex: 1;
    &.responsible {
      flex: 1.05;
    }
    &.date {
      flex: 0.925;
      &.is-mobile {
        margin-top: 10px;
      }
    }

    &.description {
      flex: 2;
    }
    margin-left: 10px;
    margin-right: 10px;
    .label {
      font-size: 12px;
    }
  }
  .textarea {
    background: white;
    border-radius: 2px;
  }
  .buttons {
    margin: 0;
    margin-top: 10px;
    margin-left: auto;
    max-width: 118px;
    &.is-mobile {
      margin-top: 1.2rem;
      margin-right: 0.7rem;
    }
    .button {
      border-radius: 2px;
      width: 110px;
    }
  }
}
.no-list {
  padding-top: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid $lightest-grey;
  text-align: center;
}
.spacer {
  width: 104px;
}

.lists.columns {
  margin-top: 10px;
}
</style>

<style lang="scss">
@import "@/assets/colors.scss";

.multiselect.panel-select.role {
  flex: 1;
  .caret {
    background-color: #fcfcfc;
  }
  .multiselect__tags {
    background-color: white;

    .multiselect__single,
    .multiselect__input {
      background-color: white;
    }
  }
  .multiselect__content {
    background: white;
  }
}

.input.date-input {
  background: white;
  border-radius: 2px;
}

.vc-bg-orange-600 {
  background-color: $info !important;
}
.vc-day-content:focus {
  background-color: $info !important;
  color: $white;
}
</style>
